import React, { useState, useEffect, useRef } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faShoppingCart,
  faUsers,
  faMoneyBillWave,
  faClock,
  faArrowUp,
  faArrowDown,
  faSync,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './RealTimeSales.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RealTimeSales = () => {
  const [realTimeData, setRealTimeData] = useState({
    currentSales: 0,
    totalSales: 0,
    activeUsers: 0,
    avgOrderValue: 0,
    salesTrend: [],
    topProducts: [],
    recentOrders: [],
    userActivity: [],
    salesByHour: [],
    conversionRate: 0
  });

  const [timeRange, setTimeRange] = useState('today'); // today, week, month
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const chartRef = useRef(null);

  // Real-time data fetching
  useEffect(() => {
    fetchRealTimeData();
    
    let interval;
    if (isLive) {
      // Update every 30 seconds instead of 5 to reduce constant refreshing
      interval = setInterval(() => {
        fetchRealTimeData();
        setLastUpdate(new Date());
      }, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeRange, isLive]);

  const fetchRealTimeData = async () => {
    try {
      const response = await api.get(`/admin/analytics/realtime?range=${timeRange}`);
      setRealTimeData(response.data);
    } catch (error) {
      console.error('Error fetching real-time data:', error);
    }
  };

  // Sales trend chart (real-time line chart)
  const salesTrendChartData = {
    labels: realTimeData.salesTrend.map(item => {
      const date = new Date(item.time);
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }),
    datasets: [
      {
        label: 'Ventes (FCFA)',
        data: realTimeData.salesTrend.map(item => item.amount),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6
      },
      {
        label: 'Nombre de commandes',
        data: realTimeData.salesTrend.map(item => item.orders),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]
  };

  const salesTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#e5e7eb',
          font: { size: 12 }
        }
      },
      title: {
        display: true,
        text: 'Tendance des ventes en temps réel',
        color: '#f3f4f6',
        font: { size: 16, weight: 'bold' }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#f3f4f6',
        bodyColor: '#e5e7eb',
        borderColor: '#374151',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      },
      y: {
        type: 'linear',
        position: 'left',
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      },
      y1: {
        type: 'linear',
        position: 'right',
        ticks: { color: '#9ca3af' },
        grid: { display: false }
      }
    },
    animation: {
      duration: 750
    }
  };

  // Sales by hour chart
  const salesByHourData = {
    labels: realTimeData.salesByHour.map(item => `${item.hour}h`),
    datasets: [
      {
        label: 'Ventes par heure',
        data: realTimeData.salesByHour.map(item => item.amount),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 2
      }
    ]
  };

  const salesByHourOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Distribution des ventes par heure',
        color: '#f3f4f6',
        font: { size: 14 }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af' },
        grid: { display: false }
      },
      y: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      }
    }
  };

  // Top products doughnut chart
  const topProductsData = {
    labels: realTimeData.topProducts.map(p => p.name),
    datasets: [
      {
        data: realTimeData.topProducts.map(p => p.quantity),
        backgroundColor: [
          '#3b82f6',
          '#10b981',
          '#f59e0b',
          '#ef4444',
          '#8b5cf6'
        ],
        borderColor: '#1f2937',
        borderWidth: 2
      }
    ]
  };

  const topProductsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#e5e7eb',
          font: { size: 11 },
          padding: 10
        }
      },
      title: {
        display: true,
        text: 'Top 5 Produits',
        color: '#f3f4f6',
        font: { size: 14 }
      }
    }
  };

  // User activity chart
  const userActivityData = {
    labels: realTimeData.userActivity.map(item => item.name),
    datasets: [
      {
        label: 'Activité',
        data: realTimeData.userActivity.map(item => item.actions),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: '#10b981',
        borderWidth: 2
      }
    ]
  };

  const userActivityOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Utilisateurs actifs',
        color: '#f3f4f6',
        font: { size: 14 }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      },
      y: {
        ticks: { color: '#9ca3af' },
        grid: { display: false }
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="realtime-sales-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faChartLine} /> Tableau de Bord en Temps Réel
          </h1>
          <div className="live-indicator">
            <span className={`status-dot ${isLive ? 'active' : 'inactive'}`}></span>
            {isLive ? 'EN DIRECT' : 'PAUSE'}
            <span className="last-update">Mise à jour: {formatTime(lastUpdate)}</span>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="time-range-selector">
            <button
              className={timeRange === 'today' ? 'active' : ''}
              onClick={() => setTimeRange('today')}
            >
              Aujourd'hui
            </button>
            <button
              className={timeRange === 'week' ? 'active' : ''}
              onClick={() => setTimeRange('week')}
            >
              Semaine
            </button>
            <button
              className={timeRange === 'month' ? 'active' : ''}
              onClick={() => setTimeRange('month')}
            >
              Mois
            </button>
          </div>
          
          <button
            className={`live-toggle ${isLive ? 'active' : ''}`}
            onClick={() => setIsLive(!isLive)}
          >
            <FontAwesomeIcon icon={isLive ? faBolt : faSync} />
            {isLive ? 'Live' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon blue">
            <FontAwesomeIcon icon={faShoppingCart} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Ventes Actuelles</div>
            <div className="kpi-value">{formatCurrency(realTimeData.currentSales)}</div>
            <div className="kpi-change positive">
              <FontAwesomeIcon icon={faArrowUp} /> +12.5%
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon green">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Total Ventes</div>
            <div className="kpi-value">{formatCurrency(realTimeData.totalSales)}</div>
            <div className="kpi-change positive">
              <FontAwesomeIcon icon={faArrowUp} /> +8.3%
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon purple">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Utilisateurs Actifs</div>
            <div className="kpi-value">{realTimeData.activeUsers}</div>
            <div className="kpi-change positive">
              <FontAwesomeIcon icon={faArrowUp} /> +5
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon orange">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Valeur Moyenne</div>
            <div className="kpi-value">{formatCurrency(realTimeData.avgOrderValue)}</div>
            <div className="kpi-change negative">
              <FontAwesomeIcon icon={faArrowDown} /> -2.1%
            </div>
          </div>
        </div>
      </div>

      {/* Main Charts */}
      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-container">
            <Line data={salesTrendChartData} options={salesTrendOptions} />
          </div>
        </div>
      </div>

      {/* Secondary Charts */}
      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-container medium">
            <Bar data={salesByHourData} options={salesByHourOptions} />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-container medium">
            <Doughnut data={topProductsData} options={topProductsOptions} />
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-container medium">
            <Bar data={userActivityData} options={userActivityOptions} />
          </div>
        </div>
      </div>

      {/* Recent Orders and Analytics */}
      <div className="bottom-row">
        <div className="recent-orders">
          <h3>Commandes Récentes</h3>
          <div className="orders-list">
            {realTimeData.recentOrders.map((order, index) => (
              <div key={index} className="order-item">
                <div className="order-info">
                  <span className="order-number">#{order.numero}</span>
                  <span className="order-client">{order.client}</span>
                </div>
                <div className="order-details">
                  <span className="order-amount">{formatCurrency(order.amount)}</span>
                  <span className="order-time">{formatTime(order.date)}</span>
                </div>
                <div className={`order-status ${order.status}`}>
                  {order.status === 'paye' ? 'Payé' : 'En attente'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-summary">
          <h3>Indicateurs Clés</h3>
          <div className="metrics-list">
            <div className="metric-item">
              <span className="metric-label">Taux de Conversion</span>
              <div className="metric-bar">
                <div
                  className="metric-fill"
                  style={{ width: `${realTimeData.conversionRate}%` }}
                ></div>
              </div>
              <span className="metric-value">{realTimeData.conversionRate}%</span>
            </div>
            
            <div className="metric-item">
              <span className="metric-label">Commandes/Heure</span>
              <div className="metric-bar">
                <div className="metric-fill" style={{ width: '65%' }}></div>
              </div>
              <span className="metric-value">
                {Math.round(realTimeData.recentOrders.length / 24)}
              </span>
            </div>
            
            <div className="metric-item">
              <span className="metric-label">Taux de Retour</span>
              <div className="metric-bar">
                <div className="metric-fill negative" style={{ width: '8%' }}></div>
              </div>
              <span className="metric-value">8%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeSales;
